import "./ApplianceDetails.scss";

import React, { useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { ProductPrice, ProductStock, Rating } from "@sbi-international/boutik.gatsby.plugin.components";
import { AddItemButton } from "@sbi-international/boutik.gatsby.plugin.checkout";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { scrollToRef } from "../../utils";
import { ProductPageTemplateInfo } from "../../models/productPageTemplateInfo";
import ApplianceDetailsMadeIn from "./ApplianceDetailsMadeIn";

import { DroletButton } from "../primitives";
import { icons } from "../../constants/icons";
import PriceNotice from "./PriceNotice";

export default function ApplianceDetails(props: {
    appliance: ProductPageTemplateInfo;
    reviews: Queries.allProductReviewsNodeFragmentFragment;
    reviewsRef: React.MutableRefObject<any>;
    infoRef: React.MutableRefObject<any>;
    openOptions: any;
}) {
    const { allProductsJson } = useStaticQuery(
        graphql`
            query {
                allProductsJson(filter: { code: { eq: "drolet_carte-superficie-chauffage" } }) {
                    nodes {
                        title {
                            fr_CA
                            en_CA
                            en_US
                        }
                        documents {
                            fr_CA
                            en_CA
                            en_US
                        }
                    }
                }
            }
        `
    );

    const pdfSuperficie = allProductsJson?.nodes[0];
    const { appliance, reviews, infoRef, reviewsRef, openOptions } = props;
    const { currentLocale } = usePageLocalization();

    const { t } = useTranslation("layout");

    return (
        <>
            <div className="appliance-details-container">
                <h1 className="appliance-details-title">
                    {appliance?.static?.title?.[currentLocale]}
                </h1>
                <span className="appliance-details-code">{appliance.static.code}</span>
                {reviews && reviews.count != 0 ? (
                    <div className="appliance-details-section rating-section">
                        <div className="rating">
                            <Rating.RatingScore
                                ratingScore={reviews?.average ?? 0}
                                ratingCount={reviews?.count ?? 0}
                            />
                            <span
                                className="see-ratings"
                                onClick={() => {
                                    scrollToRef(reviewsRef, 140);
                                }}
                            >
                                {t("appliance.see-reviews")}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="appliance-details-section rating-section">
                        <div className="rating">
                            <span
                                className="see-ratings"
                                style={{ marginLeft: 0 }}
                                onClick={() => {
                                    scrollToRef(reviewsRef, 140);
                                }}
                            >
                                {t("appliance.review-product")}
                            </span>
                        </div>
                    </div>
                )}

                <div className="appliance-details-section price-section">                    
                    <ProductPrice
                        currentPrice={appliance?.live?.currentPrice}
                        prefixClassName="price-section"
                    />
                </div>
                {appliance.live?.currentStock?.specialPriceEndDate && (
                    <div className="appliance-details-section price-section">
                        <span className="promo-text">
                            {t("appliance.valid-until")}{" "}
                            {appliance.live?.currentStock?.specialPriceEndDate}
                        </span>
                    </div>
                )}
                <PriceNotice product={appliance.live} />
                <div className="appliance-details-section appliance-details-spect">
                    {(appliance.live?.isMadeInCanada || appliance.live?.isMadeInUSA) && (
                        <div className="details-spect made-in">
                            <span className="spect-title">{t("appliance.made-in")}</span>
                            <span className="spect-value">
                                <ApplianceDetailsMadeIn
                                    isMadeInCanada={appliance.live?.isMadeInCanada}
                                    isMadeInUSA={appliance.live?.isMadeInUSA}
                                />
                            </span>
                        </div>
                    )}

                    {renderSpecs(
                        appliance?.static?.category?.split("/")[3] ?? "",
                        appliance?.static?.code !== "DB00400"
                    )}
                </div>
                <div className="appliance-details-section appliance-details-availability">
                    <ProductStock
                        currentStock={appliance?.live?.currentStock}
                        type="small"
                        isCustomMade={false}
                    />
                </div>
                <div className="appliance-details-section add-to-cart-section">
                    
                    <AddItemButton
                        type={"large"}
                        product={appliance.live}
                        pluginCheckoutIconsModel={icons.pluginCheckoutIconsLarge}                        
                    />
                </div>
                <div className="appliance-details-section">
                    <DroletButton
                        label={t("appliance.choose-options") ?? ""}
                        onClick={() => {
                            infoRef.current.scrollIntoView();
                            openOptions();
                        }}
                    />
                </div>
            </div>
        </>
    );

    function renderSpecs(type: string, showPdf = true) {
        let attributes: string[] = [];
        let displayCarteSuperficie = false;

        switch (type) {
            case "Wood Stoves":
                displayCarteSuperficie = true;
                attributes = ["Surfi", "size", "LongBuci", "Emiss"];
                break;
            case "Pellet Stoves":
                displayCarteSuperficie = true;
                attributes = ["Surfi", "size", "TempComb"];
                // capacité de trémie ??
                break;
            case "Camp Stove":
                displayCarteSuperficie = true;
                attributes = ["Surfi", "size", "LongBuci", "Emiss"];
                break;
            case "Cookstoves":
                attributes = ["size", "LongBuci", "Dim_four"];
                break;

            case "Wood Inserts":
                displayCarteSuperficie = true;
                attributes = ["Surfi", "size", "LongBuci", "Emiss"];
                break;

            case "Wood Furnaces":
                displayCarteSuperficie = true;
                attributes = ["Surfi", "LongBuci", "TempComb"];
                break;

            case "Outdoor Fireplaces":
                attributes = ["Dim_four", "LongBuci"];
                break;

            case "Gas Wall Mounted Room Heater":
                attributes = ["Surfi", "PWMiEGPi"];
                break;

            default:
                break;
        }

        return (
            <React.Fragment>
                {attributes.map((attr, index) => {
                    if (attr === "size") {
                        const L =
                            appliance?.static?.attributes?.DimExHLi?.values?.[0]?.[currentLocale] ??
                            "";
                        const P =
                            appliance?.static?.attributes?.DimExHPi?.values?.[0]?.[currentLocale] ??
                            "";
                        const H =
                            appliance?.static?.attributes?.DimExHHi?.values?.[0]?.[currentLocale] ??
                            "";
                        const dimensions =
                            currentLocale === "fr_CA"
                                ? `${L} L X ${P} P X ${H} H`
                                : `${L} W X ${P} D X ${H} H`;
                        return (
                            <div className="details-spect" key={index}>
                                <span className="spect-title">
                                    {
                                        (appliance.static?.attributes as Record<string, any>)?.[
                                            attr
                                        ]?.name?.[currentLocale]
                                    }
                                </span>
                                <span className="spect-value">{dimensions}</span>
                            </div>
                        );
                    } else {
                        return (
                            <div className="details-spect" key={index}>
                                <span className="spect-title">
                                    {
                                        (appliance.static?.attributes as Record<string, any>)?.[
                                            attr
                                        ]?.name?.[currentLocale]
                                    }
                                </span>
                                <span className="spect-value">
                                    {
                                        (appliance.static?.attributes as Record<string, any>)?.[
                                            attr
                                        ]?.values?.[0]?.[currentLocale]
                                    }
                                </span>

                                {showPdf && attr === "Surfi" && displayCarteSuperficie && (
                                    <div className="carte-superficie">
                                        <FontAwesomeIcon icon={faFileAlt} className="icon" />

                                        <a
                                            className="document"
                                            rel="noreferrer"
                                            target="_blank"
                                            href={`/file/${pdfSuperficie?.documents?.[0][currentLocale]}`}
                                        >
                                            {pdfSuperficie.title[currentLocale]}
                                        </a>
                                    </div>
                                )}
                            </div>
                        );
                    }
                })}
            </React.Fragment>
        );
    }
}
